import { useMemo } from "react"

import { useQueryParams } from "@/hooks/useQueryParams"

import { NOTEBOOK_BUILDER_PATH } from "../utils/generatedNotebooks"
import { createReportRequest } from "../utils/reportClient"
import { createReportRevisionStream } from "../utils/reportRevisionStream"
import type { ReportRevisionStream } from "../utils/reportRevisionTypes"
import type { Cadence, Notebook } from "../utils/reportTypes"

export interface ReportEnvironment {
  request: ReturnType<typeof createReportRequest>
  streamRevision?: ReportRevisionStream
  notebookUrl: (entry: Notebook, view: boolean) => string
  updateLocation: (entry?: Notebook, revision?: number) => void
  sharingEnabled: boolean
  /** Cadences this environment can schedule; every cadence when omitted. */
  scheduleCadences?: Cadence[]
  /** Most agents a manual recipe may name; defaults to the hosted limit. */
  messages: {
    generated: string
    revised: (revision: number) => string
    scheduled: (cadence: Cadence) => string
  }
}

/** Keep the authenticated app boundary separate from isolated component previews. */
export function useReportEnvironment(
  organizationSlug: string,
  workspaceId: string,
  override?: ReportEnvironment,
): ReportEnvironment {
  const queryParams = useQueryParams()
  const request = useMemo(
    () =>
      override?.request ?? createReportRequest(organizationSlug, workspaceId),
    [organizationSlug, workspaceId, override?.request],
  )
  const streamRevision = useMemo(
    () => createReportRevisionStream(organizationSlug, workspaceId),
    [organizationSlug, workspaceId],
  )
  return (
    override ?? {
      request,
      streamRevision,
      sharingEnabled: true,
      notebookUrl: (entry, view) => {
        const params = new URLSearchParams({ notebook: entry.id })
        if (view) {
          params.set("view", "1")
          params.set("revision", String(entry.revision))
        }
        return new URL(
          queryParams.getHref(
            `${NOTEBOOK_BUILDER_PATH}?${params}`,
            organizationSlug,
            workspaceId,
          ),
          window.location.origin,
        ).toString()
      },
      updateLocation: (entry, revision) => {
        queryParams.setMultiple(
          {
            notebook: entry?.id ?? null,
            recipe: entry ? null : "1",
            revision: revision ? String(revision) : null,
            view: null,
          },
          { history: "replace", shallow: true },
        )
      },
      messages: {
        generated:
          "Notebook generated and saved. Ask Sofia to shape it further.",
        revised: (revision) =>
          `Sofia saved revision ${revision}. The previous version is in history.`,
        scheduled: (cadence) =>
          `Schedule saved. It runs ${cadence} while the local server is open.`,
      },
    }
  )
}
